import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Play, ExternalLink } from 'lucide-react';

const TrainingSnapshotVideo = () => {
  const videoRef = useRef(null);
  const [showEndScreen, setShowEndScreen] = useState(false);

  // Custom thumbnail for social media sharing (first title card of the video)
  const SOCIAL_THUMBNAIL = "https://resiligent.com/social/training-snapshot.jpg";

  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      video.focus();

      const handleEnded = () => {
        setShowEndScreen(true);
      };

      video.addEventListener('ended', handleEnded);
      return () => video.removeEventListener('ended', handleEnded);
    }
  }, []);

  const handleReplay = () => {
    setShowEndScreen(false);
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
      videoRef.current.play();
    }
  };

  return (
    <div className="fixed inset-0 bg-black flex items-center justify-center" data-testid="training-snapshot-page">
      <Helmet>
        <title>Training Highlights | RESILIGENT</title>
        <meta name="description" content="A short snapshot of selected highlights from our leadership training programmes. Powered by RESILIGENT." />

        {/* Open Graph / Facebook / LinkedIn / WhatsApp */}
        <meta property="og:type" content="video.other" />
        <meta property="og:title" content="RESILIGENT - Selected Training Highlights" />
        <meta property="og:description" content="A short snapshot of selected highlights from our leadership training programmes." />
        <meta property="og:image" content={SOCIAL_THUMBNAIL} />
        <meta property="og:image:width" content="1920" />
        <meta property="og:image:height" content="1080" />
        <meta property="og:site_name" content="RESILIGENT" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="RESILIGENT - Selected Training Highlights" />
        <meta name="twitter:description" content="A short snapshot of selected highlights from our leadership training programmes." />
        <meta name="twitter:image" content={SOCIAL_THUMBNAIL} />
      </Helmet>

      <video
        ref={videoRef}
        className="w-full h-full object-contain"
        controls
        playsInline
        preload="auto"
        poster="/social/training-snapshot.jpg"
        src="/videos/training-snapshot.mp4"
        data-testid="training-snapshot-video"
      >
        Your browser does not support the video tag.
      </video>

      {/* End Screen Overlay */}
      {showEndScreen && (
        <div className="absolute inset-0 bg-black/95 flex items-center justify-center z-10" data-testid="training-snapshot-endscreen">
          <div className="text-center px-6 max-w-lg">
            {/* RESILIGENT Logo */}
            <img
              src="/opt/brand/resiligent-logo-rebrand-06.webp"
              alt="RESILIGENT"
              className="h-12 sm:h-16 w-auto mx-auto mb-8"
            />

            <p className="text-white/80 text-lg sm:text-xl mb-8">
              Ready to transform your organisation&apos;s leadership culture?
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="https://resiligent.com"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#00A6DA] text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-[#0090c0] transition-all duration-300"
                data-testid="training-snapshot-visit-btn"
              >
                Visit RESILIGENT
                <ExternalLink size={20} />
              </a>

              <button
                onClick={handleReplay}
                className="inline-flex items-center gap-2 bg-white/10 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white/20 transition-all duration-300 border border-white/20"
                data-testid="training-snapshot-replay-btn"
              >
                <Play size={20} />
                Replay Video
              </button>
            </div>

            {/* Contact info */}
            <p className="text-white/50 text-sm mt-10">
              Questions? Contact us at{' '}
              <a href="mailto:info@resiligent.com" className="text-[#00A6DA] hover:underline">
                info@resiligent.com
              </a>
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrainingSnapshotVideo;
